import React, { useState } from 'react';
import {
    Clock, Video, Phone, MapPin, Link2, MoreHorizontal, Edit2, Trash2,
    Copy, ExternalLink, Calendar, ToggleLeft, ToggleRight, Users
} from 'lucide-react';
import { motion } from 'framer-motion';
import { deleteEventType, updateEventType } from '../api/calendar';
import ConfirmModal from './ConfirmModal';

const EventTypeCard = ({ eventType, onEdit, onDelete, onBook }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const getLocationIcon = () => {
        switch (eventType.location_type) {
            case 'video': return Video;
            case 'phone': return Phone;
            case 'in_person': return MapPin;
            default: return Link2;
        }
    };

    const LocationIcon = getLocationIcon();

    const handleDeleteClick = () => {
        setShowMenu(false);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteEventType(eventType.id);
            onDelete?.(eventType.id);
            setShowDeleteConfirm(false);
        } catch (err) {
            console.error('Failed to delete event type:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleActive = async () => {
        setIsToggling(true);
        try {
            const res = await updateEventType(eventType.id, { is_active: !eventType.is_active });
            // Update parent with new state
            if (onEdit) {
                onEdit({ ...eventType, is_active: !eventType.is_active });
            }
        } catch (err) {
            console.error('Failed to toggle event type:', err);
            alert('Failed to update event type: ' + err.message);
        } finally {
            setIsToggling(false);
        }
    };

    const copyBookingLink = () => {
        const baseUrl = window.location.origin;
        const link = `${baseUrl}/book/${eventType.slug}`;
        navigator.clipboard.writeText(link);
        setShowMenu(false);
        // Could add a toast notification here
    };

    return (
        <div className={`relative p-5 rounded-xl border transition-all group ${
            eventType.is_active
                ? 'dark:bg-slate-900/50 bg-white dark:border-slate-800 border-slate-200 hover:border-sky-500/50 dark:hover:bg-slate-800/50 hover:bg-slate-50'
                : 'dark:bg-slate-900/30 bg-slate-50 dark:border-slate-800/50 border-slate-200/50 opacity-75'
        }`}>
            {/* Color Strip */}
            <div
                className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
                style={{ backgroundColor: eventType.color || '#3B82F6' }}
            />

            {/* Header */}
            <div className="flex items-start justify-between mb-4 mt-1">
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold dark:text-white text-slate-900 truncate">
                        {eventType.title}
                    </h3>
                    {eventType.description && (
                        <p className="text-sm dark:text-slate-400 text-slate-500 mt-1 line-clamp-2">
                            {eventType.description}
                        </p>
                    )}
                </div>

                {/* Menu Button */}
                <div className="relative">
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white dark:hover:bg-slate-700 hover:bg-slate-200 transition opacity-0 group-hover:opacity-100"
                    >
                        <MoreHorizontal size={18} />
                    </button>

                    {/* Dropdown Menu */}
                    {showMenu && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowMenu(false)}
                            />
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                className="absolute right-0 top-full mt-1 w-48 rounded-lg border dark:bg-slate-800 bg-white dark:border-slate-700 border-slate-200 shadow-xl z-20 py-1"
                            >
                                <button
                                    onClick={() => {
                                        setShowMenu(false);
                                        onEdit?.();
                                    }}
                                    className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 dark:text-slate-300 text-slate-700 dark:hover:bg-slate-700 hover:bg-slate-100 transition"
                                >
                                    <Edit2 size={14} />
                                    Edit
                                </button>
                                <button
                                    onClick={copyBookingLink}
                                    className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 dark:text-slate-300 text-slate-700 dark:hover:bg-slate-700 hover:bg-slate-100 transition"
                                >
                                    <Copy size={14} />
                                    Copy link
                                </button>
                                <button
                                    onClick={() => {
                                        window.open(`/book/${eventType.slug}`, '_blank');
                                        setShowMenu(false);
                                    }}
                                    className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 dark:text-slate-300 text-slate-700 dark:hover:bg-slate-700 hover:bg-slate-100 transition"
                                >
                                    <ExternalLink size={14} />
                                    Preview
                                </button>
                                <div className="border-t dark:border-slate-700 border-slate-200 my-1" />
                                <button
                                    onClick={handleDeleteClick}
                                    className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-red-500 dark:hover:bg-red-500/10 hover:bg-red-50 transition"
                                >
                                    <Trash2 size={14} />
                                    Delete
                                </button>
                            </motion.div>
                        </>
                    )}
                </div>
            </div>

            {/* Details */}
            <div className="space-y-2">
                {/* Duration */}
                <div className="flex items-center gap-2 text-sm dark:text-slate-400 text-slate-500">
                    <Clock size={14} className="text-sky-500" />
                    <span>{eventType.duration} minutes</span>
                </div>

                {/* Location */}
                <div className="flex items-center gap-2 text-sm dark:text-slate-400 text-slate-500">
                    <LocationIcon size={14} className="text-sky-500" />
                    <span className="capitalize">{eventType.location_type?.replace('_', ' ') || 'Video call'}</span>
                </div>

                {/* Buffer times */}
                {(eventType.buffer_before > 0 || eventType.buffer_after > 0) && (
                    <div className="flex items-center gap-2 text-sm dark:text-slate-400 text-slate-500">
                        <Clock size={14} className="text-amber-500" />
                        <span>
                            {eventType.buffer_before > 0 && `${eventType.buffer_before}m before`}
                            {eventType.buffer_before > 0 && eventType.buffer_after > 0 && ' / '}
                            {eventType.buffer_after > 0 && `${eventType.buffer_after}m after`}
                        </span>
                    </div>
                )}

                {/* Booking count */}
                {eventType.booking_count !== undefined && (
                    <div className="flex items-center gap-2 text-sm dark:text-slate-400 text-slate-500">
                        <Users size={14} className="text-purple-500" />
                        <span>{eventType.booking_count} bookings</span>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-slate-800 border-slate-200">
                {/* Active Toggle */}
                <button
                    onClick={handleToggleActive}
                    disabled={isToggling}
                    className={`flex items-center gap-2 text-sm transition ${
                        eventType.is_active
                            ? 'text-emerald-500'
                            : 'dark:text-slate-500 text-slate-400'
                    }`}
                >
                    {eventType.is_active ? (
                        <ToggleRight size={20} className="text-emerald-500" />
                    ) : (
                        <ToggleLeft size={20} />
                    )}
                    <span>{eventType.is_active ? 'Active' : 'Inactive'}</span>
                </button>

                {/* Book Button */}
                <button
                    onClick={onBook}
                    disabled={!eventType.is_active}
                    className="px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center gap-1.5 transition"
                >
                    <Calendar size={14} />
                    Book
                </button>
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Event Type"
                message={`Are you sure you want to delete "${eventType.title}"? This action cannot be undone and all associated bookings may be affected.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default EventTypeCard;
